import type { DynamicToolUIPart, UIMessage } from 'ai'
import { AdviserRole, ToolInvocationState } from './constants'
import type {
  AdviserMessage,
  AttachedFile,
  AttachedFilesMetadata,
  CitationSource,
  SavedPart,
  ToolInvocation,
  WidgetDisplay,
} from './types'

interface ToolLikePart {
  type: string
  toolName?: string
  toolCallId?: string
  state?: string
  input?: unknown
  output?: unknown
  errorText?: string
  callProviderMetadata?: unknown
}

type Fields = Record<string, unknown>

const isFields = (x: unknown): x is Fields =>
  typeof x === 'object' && x !== null && !Array.isArray(x)

/** The SDK part has no slot of its own for this, so it rides in the part's metadata. */
const DISPLAY_METADATA_KEY = 'widgetDisplay'

const displayOf = (metadata: unknown): WidgetDisplay | undefined => {
  if (!isFields(metadata)) return undefined
  const display = metadata[DISPLAY_METADATA_KEY]
  if (!isFields(display) || typeof display.label !== 'string') return undefined
  return typeof display.meta === 'string'
    ? { label: display.label, meta: display.meta }
    : { label: display.label }
}

const displayMetadata = (display: WidgetDisplay | undefined) =>
  display
    ? {
        callProviderMetadata: {
          [DISPLAY_METADATA_KEY]: display.meta
            ? { label: display.label, meta: display.meta }
            : { label: display.label },
        },
      }
    : {}

const isToolPart = (part: unknown): part is ToolLikePart => {
  if (typeof part !== 'object' || part === null) return false
  if (!('type' in part) || typeof part.type !== 'string') return false
  return (
    part.type === 'dynamic-tool' ||
    (part.type.startsWith('tool-') && 'state' in part)
  )
}

/**
 * Extract plain text content from a UIMessage's parts array.
 */
export const getMessageContent = (
  msg: UIMessage,
  { dedupeRepeatedText = false }: { dedupeRepeatedText?: boolean } = {}
): string => {
  let previousText: string | undefined
  return msg.parts
    .filter((p): p is Extract<typeof p, { type: 'text' }> => p.type === 'text')
    .map((p) => p.text)
    .filter((part) => {
      if (!dedupeRepeatedText) return true
      const text = part.trim()
      if (text === previousText) return false
      previousText = text
      return true
    })
    .join('\n\n')
}

export const widgetCallIdOf = (
  msg: AdviserMessage,
  hasWidget: (toolName: string) => boolean
): string | null => {
  for (const part of msg.retrievalContext?.parts ?? []) {
    if (part.type === 'tool-call' && hasWidget(part.tool)) return part.id
  }
  return null
}

export const sourcesByWidget = (
  toolInvocations: ToolInvocation[],
  sourcesMap: Readonly<Record<string, CitationSource[]>>
): Record<string, CitationSource[]> => {
  const byWidget: Record<string, CitationSource[]> = {}
  for (const invocation of toolInvocations) {
    const callId = invocation.toolCallId
    if (!callId) continue
    const cited = sourcesMap[callId]
    if (cited) byWidget[callId] = cited
  }
  return byWidget
}

/**
 * Answering a widget continues the same message, so a message can hold several
 * widgets. Text before the first stays in the bubble, text between two widgets
 * draws above the later one, and text after the last draws below it.
 */
export const splitTextAroundWidget = (
  msg: UIMessage,
  hasWidget: (toolName: string) => boolean
): {
  before: string
  beforeWidget: Record<string, string>
  after: string
} => {
  const before: string[] = []
  const beforeWidget: Record<string, string> = {}
  let segment = before
  let widgetsSeen = 0
  for (const part of msg.parts) {
    if (isToolPart(part)) {
      const toolName = part.toolName ?? part.type.replace(/^tool-/, '')
      if (
        !hasWidget(toolName) ||
        part.state === ToolInvocationState.OutputError
      )
        continue
      if (widgetsSeen > 0 && segment.length > 0 && part.toolCallId)
        beforeWidget[part.toolCallId] = segment.join('\n\n')
      widgetsSeen += 1
      segment = []
      continue
    }
    if (part.type !== 'text') continue
    const text = (part as { text?: string }).text ?? ''
    if (text.length > 0) segment.push(text)
  }
  const after = widgetsSeen > 0 ? segment : []
  return {
    before: before.join('\n\n'),
    beforeWidget,
    after: after.join('\n\n'),
  }
}

/**
 * Extract tool invocation parts from a UIMessage.
 * Server-side tools arrive as static parts (type "tool-{name}") or dynamic parts.
 */
const isRestoredMessage = (msg: UIMessage): boolean =>
  typeof msg.metadata === 'object' &&
  msg.metadata !== null &&
  'restored' in msg.metadata &&
  msg.metadata.restored === true

export const getToolInvocations = (msg: UIMessage): ToolInvocation[] => {
  const results: ToolInvocation[] = []
  const restored = isRestoredMessage(msg)
  for (const part of msg.parts) {
    if (!isToolPart(part)) continue

    const toolName = part.toolName ?? part.type.replace(/^tool-/, '')
    const state = part.state ?? ''
    const display = displayOf(part.callProviderMetadata)
    results.push({
      toolName,
      toolCallId:
        typeof part.toolCallId === 'string' ? part.toolCallId : undefined,
      state,
      result:
        state === 'output-available'
          ? part.output
          : state === 'output-error'
            ? part.errorText
            : undefined,
      input: part.input,
      restored,
      ...(display ? { display } : {}),
    })
  }
  return results
}

/**
 * Folds a stored role into the two the chat UI and the posted-back history accept.
 * Hydration and the citation reconciliation both pair UI bubbles against database
 * rows by position, so a role either one folds differently shifts every later
 * pairing by one.
 */
export const toChatRole = (
  role: string
): AdviserRole.User | AdviserRole.Assistant =>
  role === AdviserRole.User ? AdviserRole.User : AdviserRole.Assistant

const restoredOutput = (output: unknown): string =>
  typeof output === 'string' ? output : (JSON.stringify(output) ?? '')

/** An answered widget call, in the one shape both a reload from the server and an answer in the browser give it. */
export const answeredToolPart = (
  call: { toolCallId: string; toolName: string; input: unknown },
  output: string,
  display: WidgetDisplay | undefined
): DynamicToolUIPart => ({
  type: 'dynamic-tool',
  ...call,
  state: ToolInvocationState.OutputAvailable,
  output,
  ...displayMetadata(display),
})

/** A persisted turn's tool calls as parts. A call with no result stays open, so its widget redraws. */
export const restoredToolParts = (msg: AdviserMessage): DynamicToolUIPart[] => {
  const saved = msg.retrievalContext?.parts ?? []
  if (saved.length > 0) {
    const results = new Map(
      saved.flatMap((part) =>
        part.type === 'tool-result' ? [[part.id, part] as const] : []
      )
    )
    return saved.flatMap((part): DynamicToolUIPart[] => {
      if (part.type !== 'tool-call') return []
      const result = results.get(part.id)
      if (!result) {
        return [
          {
            type: 'dynamic-tool',
            toolCallId: part.id,
            toolName: part.tool,
            state: 'input-available',
            input: part.input,
          },
        ]
      }
      return [
        result.error
          ? {
              type: 'dynamic-tool',
              toolCallId: part.id,
              toolName: part.tool,
              state: 'output-error',
              input: part.input,
              errorText: restoredOutput(result.output),
            }
          : answeredToolPart(
              { toolCallId: part.id, toolName: part.tool, input: part.input },
              restoredOutput(result.output),
              result.display
            ),
      ]
    })
  }

  // Rows saved before turns recorded their pieces kept only results, so the id is a stand-in.
  return (msg.retrievalContext?.toolResults ?? []).map((result) => ({
    type: 'dynamic-tool',
    toolCallId: `restored-${msg.id}-${result.toolName}`,
    toolName: result.toolName,
    state: 'output-available',
    input: undefined,
    output: restoredOutput(result.result),
  }))
}

type MessagePart = UIMessage['parts'][number]

/** Saved parts arrive unchecked from the server, so a malformed one is skipped rather than failing the whole restore. */
const isSavedText = (
  part: SavedPart
): part is Extract<SavedPart, { type: 'text' }> =>
  part.type === 'text' && typeof part.text === 'string'

/** The server saves only the last step's text as content, so a reply that ends on a tool call keeps its text only in the saved parts. */
export const restoredMessageParts = (msg: AdviserMessage): MessagePart[] => {
  const saved = msg.retrievalContext?.parts ?? []
  const savedText = saved.some(
    (part) => isSavedText(part) && part.text.trim().length > 0
  )
  if (msg.content.trim().length > 0 || !savedText)
    return [{ type: 'text', text: msg.content }, ...restoredToolParts(msg)]

  const tools = new Map(
    restoredToolParts(msg).map((part) => [part.toolCallId, part])
  )
  return saved.flatMap((part): MessagePart[] => {
    if (isSavedText(part)) return [{ type: 'text', text: part.text }]
    if (part.type !== 'tool-call') return []
    const tool = tools.get(part.id)
    return tool ? [tool] : []
  })
}

const isCall = (part: MessagePart, toolCallId: string): boolean =>
  isToolPart(part) && part.toolCallId === toolCallId

export const findToolPart = (
  messages: UIMessage[],
  toolCallId: string
): MessagePart | undefined =>
  messages.flatMap((msg) => msg.parts).find((p) => isCall(p, toolCallId))

export const replaceToolPart = (
  messages: UIMessage[],
  toolCallId: string,
  replacement: MessagePart
): UIMessage[] =>
  messages.map((msg) =>
    msg.parts.some((p) => isCall(p, toolCallId))
      ? {
          ...msg,
          parts: msg.parts.map((p) =>
            isCall(p, toolCallId) ? replacement : p
          ),
        }
      : msg
  )

const isAttachedFile = (value: unknown): value is AttachedFile => {
  if (typeof value !== 'object' || value === null) return false
  if (!('fileName' in value) || typeof value.fileName !== 'string') return false
  if (!('contentType' in value) || typeof value.contentType !== 'string') {
    return false
  }
  if (!('previewUrl' in value)) return true
  return typeof value.previewUrl === 'string' || value.previewUrl === undefined
}

const hasAttachedFiles = (
  metadata: unknown
): metadata is AttachedFilesMetadata => {
  if (typeof metadata !== 'object' || metadata === null) return false
  if (!('attachedFiles' in metadata)) return false
  const { attachedFiles } = metadata
  return Array.isArray(attachedFiles) && attachedFiles.every(isAttachedFile)
}

/**
 * The files sent alongside a message, carried in live-session metadata only.
 * Messages rehydrated from the database have none.
 */
export const getAttachedFiles = (
  msg: UIMessage
): AttachedFile[] | undefined => {
  if (!hasAttachedFiles(msg.metadata)) return undefined
  return msg.metadata.attachedFiles.length > 0
    ? msg.metadata.attachedFiles
    : undefined
}

const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((entry) => typeof entry === 'string')

export interface TicketFileNames {
  attachedFiles?: string[]
  droppedAttachments?: string[]
}

export const parseTicketFileNames = (source: unknown): TicketFileNames => {
  if (typeof source !== 'object' || source === null) return {}
  const attached =
    'attachedFiles' in source && isStringArray(source.attachedFiles)
      ? source.attachedFiles
      : undefined
  const dropped =
    'droppedAttachments' in source && isStringArray(source.droppedAttachments)
      ? source.droppedAttachments
      : undefined
  return {
    ...(attached ? { attachedFiles: attached } : {}),
    ...(dropped ? { droppedAttachments: dropped } : {}),
  }
}
